import { useState, useCallback } from 'react';
import { Task } from '../../types';
import { ErrorHandler } from '../../core/errors/ErrorHandler';
import { leadHistoryService, type AddEventParams } from '../../services/leadHistoryService';
import { useNotificationsContext } from '../NotificationsContext';
import { mapDomainTaskToFrontend, resolveAssignedToId } from './taskMapper';

import {
    createTaskUseCase,
    updateTaskUseCase,
    deleteTaskUseCase,
    repositories
} from '../../infrastructure/di/crmContainer';

const taskRepository = repositories.task;

/** Registra evento no histórico do lead (DRY — usado em create/toggle/delete). */
function trackTaskHistory(
    orgId: string | undefined,
    leadId: string | null | undefined,
    type: AddEventParams['type'],
    description: string,
    value?: string
): void {
    if (!orgId || !leadId) return;
    leadHistoryService.addEvent({
        leadId,
        organizationId: orgId,
        type,
        description,
        ...(type === 'task_deleted' ? { oldValue: value } : { newValue: value })
    });
}

interface UseTaskActionsParams {
    organizationId?: string;
}

export function useTaskActions({ organizationId }: UseTaskActionsParams) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { addNotification } = useNotificationsContext();

    const refreshTasks = useCallback(async () => {
        try {
            const domainTasks = await taskRepository.findAll(organizationId);
            setTasks(domainTasks.map(mapDomainTaskToFrontend));
        } catch (error) {
            ErrorHandler.handle(error as Error, 'RefreshTasks');
        }
    }, [organizationId]);

    const addTask = useCallback(async (task: Omit<Task, 'id'>) => {
        try {
            const newDomainTask = await createTaskUseCase.execute({
                leadId: task.leadId || null,
                title: task.title,
                dueDate: new Date(task.dueDate),
                description: task.description,
                type: task.type,
                organizationId,
                assignedTo: resolveAssignedToId(task.assignedTo)
            });

            const newFrontendTask = mapDomainTaskToFrontend(newDomainTask);
            setTasks(prev => [...prev, newFrontendTask]);

            addNotification({
                title: 'Nova tarefa criada',
                message: `"${newFrontendTask.title}" - ${newFrontendTask.lead?.name || 'Sem negociação'}`,
                type: 'info',
                tag: `task-created-${newFrontendTask.id}`,
                recipientUserId: newFrontendTask.assignedTo?.id,
                recipientUserName: newFrontendTask.assignedTo?.name,
            });

            trackTaskHistory(organizationId, task.leadId, 'task_created', 'Tarefa criada', newFrontendTask.title);
        } catch (error) {
            ErrorHandler.handle(error as Error, 'AddTask');
        }
    }, [organizationId, addNotification]);

    const updateTask = useCallback(async (updatedTask: Task) => {
        try {
            await updateTaskUseCase.execute({
                id: updatedTask.id,
                title: updatedTask.title,
                dueDate: updatedTask.dueDate ? new Date(updatedTask.dueDate) : undefined,
                isCompleted: updatedTask.isCompleted,
                description: updatedTask.description
            });
            await refreshTasks();
        } catch (error) {
            ErrorHandler.handle(error as Error, 'UpdateTask');
        }
    }, [refreshTasks]);

    const toggleTaskCompletion = useCallback(async (taskId: string) => {
        const task = tasks.find(t => t.id === taskId);
        if (!task) return;

        const updatedTask = { ...task, isCompleted: !task.isCompleted };
        await updateTask(updatedTask);

        const type = task.isCompleted ? 'task_uncompleted' : 'task_completed';
        const desc = task.isCompleted ? 'Tarefa desmarcada como concluída' : 'Tarefa marcada como concluída';
        trackTaskHistory(organizationId, task.leadId, type, desc, task.title);
    }, [tasks, updateTask, organizationId]);

    const deleteTask = useCallback(async (taskId: string) => {
        try {
            const task = tasks.find(t => t.id === taskId);
            await deleteTaskUseCase.execute(taskId);
            setTasks(prev => prev.filter(t => t.id !== taskId));

            if (task) {
                trackTaskHistory(organizationId, task.leadId, 'task_deleted', 'Tarefa deletada', task.title);
            }
        } catch (error) {
            ErrorHandler.handle(error as Error, 'DeleteTask');
        }
    }, [tasks, organizationId]);

    return { tasks, isLoading, setIsLoading, addTask, updateTask, toggleTaskCompletion, deleteTask, refreshTasks };
}
